// assets/section-help-hub.js
// Help hub topic navigator. One DOM, two behaviours:
//   - desktop (>991px): tabs — exactly one topic open; clicking a topic scrolls the page
//     to the top of its panel (clicking the open one only scrolls)
//   - mobile/tablet (≤991px): accordion — tap toggles, one open at a time, all may close
// Layout is CSS-driven; this only flips `is-active` + aria-expanded and follows the
// breakpoint via matchMedia. Re-initialises on shopify:section:load for the Theme Editor,
// and opens the topic that owns a block selected in the editor (shopify:block:select).
(() => {
  window.DigitalizModules = window.DigitalizModules || {};
  if (window.DigitalizModules.HelpHub) return;

  const MOBILE_QUERY = '(max-width: 991px)';
  const instances = new WeakMap();

  class HelpHub {
    constructor(el) {
      this.el = el;
      this.topics = Array.from(el.querySelectorAll('[data-help-hub-topic]'));
      this.mql = window.matchMedia(MOBILE_QUERY);
      this.onClick = this.onClick.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);
      this.init();
    }

    init() {
      this.el.addEventListener('click', this.onClick);
      this.mql.addEventListener('change', this.onMediaChange);
      this.onMediaChange();
    }

    get isMobile() {
      return this.mql.matches;
    }

    onClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const toggle = target.closest('[data-help-hub-toggle]');
      if (!toggle || !this.el.contains(toggle)) return;

      const topic = toggle.closest('[data-help-hub-topic]');
      if (!topic) return;

      const isActive = topic.classList.contains('is-active');
      if (this.isMobile) {
        this.setActive(isActive ? null : topic);
      } else {
        if (!isActive) this.setActive(topic);
        this.scrollToPanel(topic);
      }
    }

    // Desktop: bring the top of the topic's panel to the top of the viewport,
    // clear of the fixed announcement bar + sticky header (theme.js publishes both heights).
    scrollToPanel(topic) {
      const panel = topic.querySelector('[data-help-hub-panel]');
      if (!panel) return;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const rootStyle = getComputedStyle(document.documentElement);
      const offset =
        (parseFloat(rootStyle.getPropertyValue('--sticky-bar-offset')) || 0) +
        (parseFloat(rootStyle.getPropertyValue('--sticky-header-offset')) || 0);
      const top = panel.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: top, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    setActive(activeTopic) {
      this.topics.forEach((topic) => {
        const open = topic === activeTopic;
        topic.classList.toggle('is-active', open);
        const toggle = topic.querySelector('[data-help-hub-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    // Desktop must always show a panel; mobile keeps whatever the visitor left open.
    onMediaChange() {
      if (this.isMobile) return;
      const anyOpen = this.topics.some((topic) => topic.classList.contains('is-active'));
      if (!anyOpen && this.topics[0]) this.setActive(this.topics[0]);
    }

    // Open the topic that contains `blockEl` (a topic button or a nested question).
    activateBlock(blockEl) {
      const topic = blockEl.closest('[data-help-hub-topic]');
      if (!topic || !this.topics.includes(topic)) return;
      if (!topic.classList.contains('is-active')) this.setActive(topic);
    }

    destroy() {
      this.el.removeEventListener('click', this.onClick);
      this.mql.removeEventListener('change', this.onMediaChange);
      instances.delete(this.el);
    }
  }

  window.DigitalizModules.HelpHub = HelpHub;

  function init() {
    document.querySelectorAll('[data-help-hub]').forEach((el) => {
      if (instances.has(el)) return;
      instances.set(el, new HelpHub(el));
    });
  }

  function onSectionUnload(event) {
    event.target.querySelectorAll('[data-help-hub]').forEach((el) => {
      const instance = instances.get(el);
      if (instance) instance.destroy();
    });
  }

  function onBlockSelect(event) {
    const hubEl = event.target.closest('[data-help-hub]');
    const instance = hubEl && instances.get(hubEl);
    if (instance) instance.activateBlock(event.target);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', onSectionUnload);
  document.addEventListener('shopify:block:select', onBlockSelect);
})();
